import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Radio as AntdRadio, Row, Checkbox } from "antd";
import Button from "@dtci/spellbook/Button";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
import { InputLabel } from "tap/components/InputLabel";
import { ClearableFilterInput } from "tap/components/ClearableFilterInput";
import { useLicensingJobsData } from "../../hooks/useLicensingJobsData";
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import { validateCommaSeparatedNumericIds } from "../../utils/parseCommaSeparatedIds";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
import { buildFilterFromParams, cleanJobFilter } from "./utils";
import "./styles.scss";

/**
 * Search form for filtering the Licensing Jobs grid.
 *
 * Available filters:
 * - Licensing Job ID: exact ID or comma-separated IDs
 * - Catalog ID: exact catalog ID search
 * - Catalog Name: text search on catalog name
 * - Users: multi-select for assigned users
 * - Status: multi-select for job status
 * - Media: multi-select for media types
 * - Territory: multi-select for territories
 * - Licensee Type: checkbox group (GLOBAL/LOCAL)
 * - TPR Display: radio to hide/show/only show TPRs
 *
 * Filter state syncs bidirectionally with URL params for shareable searches.
 * Changes trigger debounced searches (400ms delay).
 */
export function JobsSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const {
    fetchLicensingJobs,
    updateJobs,
    clearJobs,
    licJobSort,
    itemsPerPage,
  } = useLicensingJobsData();

  const { updateJobsSearchForm } = useJobsActions();

  const {
    filterForm,
    jobStatusOptions,
    mediaOptions,
    territoryOptions,
    userOptions,
  } = useSelector((s) => s.licensingJobs);

  const [validationErrors, setValidationErrors] = useState({
    jobId: undefined,
    catalogId: undefined,
    catalogName: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useAsyncEffect(async ({ isMounted }) => {
    try {
      const [mediaRes, territoryRes, jobStatusRes, userRes] = await Promise.all(
        [
          tapApi.mediaAndTerritories("media"),
          tapApi.mediaAndTerritories("territory"),
          tapApi.licensingJobStatuses(),
          tapApi.users(
            { dataType: "licensingJob" },
            [0, 199],
            ["fullName", "ASC"],
          ),
        ],
      );

      if (isMounted()) {
        const media = Array.isArray(mediaRes)
          ? mediaRes.map(({ label, value }) => ({ label, value }))
          : [];
        const territories = Array.isArray(territoryRes)
          ? territoryRes.map(({ label, value }) => ({ label, value }))
          : [];
        const jobStatuses = Array.isArray(jobStatusRes)
          ? jobStatusRes.map(({ name, id }) => ({ label: name, value: id }))
          : [];
        const users =
          userRes?.content?.map(({ fullName, id }) => ({
            label: fullName,
            value: id,
          })) ?? [];
        updateJobs({
          mediaOptions: media,
          territoryOptions: territories,
          jobStatusOptions: jobStatuses,
          userOptions: users,
        });
      }
    } catch (error) {
      console.error("Failed to load initial options:", error);
      if (isMounted()) {
        updateJobs({
          mediaOptions: [],
          territoryOptions: [],
          jobStatusOptions: [],
          userOptions: [],
        });
      }
    }
  }, []);

  // Sync URL params to form state on navigation (location.key changes on each navigation)
  useEffect(() => {
    const params = parseSearchParams(location.search);

    const filterData = buildFilterFromParams(params, {
      userOptions,
      jobStatusOptions,
      mediaOptions,
      territoryOptions,
    });

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updateJobs({
        itemsPerPage: filterData.pageSize,
        licJobRange: rangeOverride,
        currentPage: 0,
      });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    if (hasUrlParams) {
      updateJobsSearchForm({ key: "filterForm", data: filterData });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Re-parse URL params when options load (multi-select values depend on options being available)
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = parseSearchParams(location.search);
    const hasMultiSelectParams =
      params.users || params.status || params.media || params.territory;

    if (
      hasMultiSelectParams &&
      (userOptions.length > 0 ||
        jobStatusOptions.length > 0 ||
        mediaOptions.length > 0 ||
        territoryOptions.length > 0)
    ) {
      const filterData = buildFilterFromParams(params, {
        userOptions,
        jobStatusOptions,
        mediaOptions,
        territoryOptions,
      });

      const filterForFetch = { ...filterForm, ...filterData };

      updateJobsSearchForm({ key: "filterForm", data: filterData });
      fetchData(filterForFetch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions, jobStatusOptions, mediaOptions, territoryOptions]);

  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchLicensingJobs(range, filterData);

    if (data) {
      updateJobs({
        jobs: data?.content,
        totalItems: data?.total,
        totalPages: Math.ceil(data?.total / data?.size) || 0,
        currentPage: data?.pageNumber ?? 0,
        licJobRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset navigates to clean URL; the location.key useEffect handles re-fetch
  const handleResetFilter = () => {
    navigate(location.pathname);
    clearJobs();
    setValidationErrors({
      jobId: undefined,
      catalogId: undefined,
      catalogName: undefined,
    });
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (field === "jobId") {
      const error = validateCommaSeparatedNumericIds(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    } else if (field === "catalogId") {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updateJobsSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const resetRange = getItemRange(0, itemsPerPage);
        updateJobs({
          currentPage: 0,
          licJobRange: resetRange,
        });
        const filtered = cleanJobFilter(
          newFilterForm,
          resetRange,
          licJobSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  const handleClearField = (field) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newValidationErrors = { ...validationErrors, [field]: undefined };
    setValidationErrors(newValidationErrors);

    const newFilterForm = { ...filterForm, [field]: "" };
    updateJobsSearchForm({
      key: "filterForm",
      data: { [field]: "" },
    });

    const resetRange = getItemRange(0, itemsPerPage);
    updateJobs({
      currentPage: 0,
      licJobRange: resetRange,
    });
    const filtered = cleanJobFilter(newFilterForm, resetRange, licJobSort);
    updateSearchParams(location, navigate, filtered);
    fetchData(newFilterForm, resetRange);
  };

  return (
    <Row className="jobs-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Licensing Job ID">
          <ClearableFilterInput
            value={filterForm?.jobId || ""}
            onChange={(e) => handleInputChange("jobId", e.target.value)}
            onClear={() => handleClearField("jobId")}
            width="auto"
            id="jobId"
            name="jobId"
            placeholder="Enter ID(s), comma-separated"
            className={validationErrors.jobId ? "input-error" : ""}
          />
          {validationErrors.jobId && (
            <div className="validation-error">{validationErrors.jobId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Catalog ID">
          <ClearableFilterInput
            value={filterForm?.catalogId || ""}
            onChange={(e) => handleInputChange("catalogId", e.target.value)}
            onClear={() => handleClearField("catalogId")}
            width="auto"
            id="catalogId"
            name="catalogId"
            placeholder="Enter"
            className={validationErrors.catalogId ? "input-error" : ""}
          />
          {validationErrors.catalogId && (
            <div className="validation-error">{validationErrors.catalogId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Catalog Name">
          <ClearableFilterInput
            value={filterForm?.catalogName || ""}
            onChange={(e) =>
              handleInputChange("catalogName", e.target.value)
            }
            onClear={() => handleClearField("catalogName")}
            width="auto"
            id="catalogName"
            name="catalogName"
            placeholder="Enter"
            className={validationErrors.catalogName ? "input-error" : ""}
          />
          {validationErrors.catalogName && (
            <div className="validation-error">
              {validationErrors.catalogName}
            </div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Users">
          <MultiSelect
            onChange={(value) => handleInputChange("users", value)}
            name="users"
            options={userOptions}
            isSearchable
            isClearable
            value={filterForm.users || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Status">
          <MultiSelect
            onChange={(value) => handleInputChange("status", value)}
            name="status"
            options={jobStatusOptions}
            isSearchable
            isClearable
            value={filterForm.status || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Media">
          <MultiSelect
            onChange={(value) => handleInputChange("media", value)}
            name="media"
            options={mediaOptions}
            isSearchable
            isClearable
            value={filterForm.media || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Territory">
          <MultiSelect
            onChange={(value) => handleInputChange("territory", value)}
            name="territory"
            options={territoryOptions}
            isSearchable
            isClearable
            value={filterForm.territory || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 32 }} label="Licensee Type">
          <Checkbox.Group
            name="licenseeType"
            value={filterForm.licenseeType || []}
            onChange={(checkedValues) =>
              handleInputChange("licenseeType", checkedValues)
            }
          >
            <div>
              <Checkbox value="GLOBAL">
                Global (Amazon, Apple, Google, Microsoft, Vubiquity)
              </Checkbox>
            </div>
            <div style={{ marginTop: "0.75rem" }}>
              <Checkbox value="LOCAL">Local (all other Licensees)</Checkbox>
            </div>
          </Checkbox.Group>
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="TPR Display">
          <AntdRadio.Group
            name="tprFlag"
            value={filterForm.tprDisplay}
            onChange={(e) => handleInputChange("tprDisplay", e.target.value)}
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <AntdRadio value="hide">Hide TPRs</AntdRadio>
            <AntdRadio value="include">Show TPRs</AntdRadio>
            <AntdRadio value="only">
              Show <span style={{ fontWeight: "bold" }}>ONLY</span> TPRs
            </AntdRadio>
          </AntdRadio.Group>
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
